import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";
import { useSEO } from "@/lib/useSEO";
import { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  useSEO({
    title: "Set New Password — The Flow State Method",
    description: "Set a new password for your Better Through Boxing account.",
    path: "/reset-password",
  });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Request a new link.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const data = await resetPassword(token, password);
      setUser(data);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed — your link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] p-6 text-white" data-testid="reset-password-page">
      <div className="w-full max-w-md border border-[#27272A] bg-[#0A0A0A] rounded-sm p-8">
        <Link to="/" className="inline-block mb-6" data-testid="reset-logo"><BrandMark size="md" /></Link>

        {done ? (
          <div data-testid="reset-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-3" />
            <h1 className="font-display text-3xl uppercase tracking-tight mb-3">Password updated.</h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-5">
              You&apos;re now signed in{user?.email ? <> as <strong className="text-white">{user.email}</strong></> : ""}. Head to your portal to manage sessions.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => navigate(user?.role === "admin" ? "/admin" : "/portal")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs" data-testid="reset-go-portal">Go to portal</Button>
              <Link to="/" data-testid="reset-go-home">
                <Button variant="outline" className="w-full border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">Homepage</Button>
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="reset-form">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Reset password</p>
            <h1 className="font-display text-3xl uppercase tracking-tight mb-3">Pick a new password.</h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              Choose something memorable but strong (min 6 chars). You&apos;ll be signed in automatically once you save.
            </p>

            <div className="space-y-2 mb-4">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">New password</Label>
              <Input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-12" data-testid="reset-new-password" />
            </div>
            <div className="space-y-2 mb-6">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Confirm password</Label>
              <Input required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-12" data-testid="reset-confirm-password" />
            </div>

            <Button type="submit" disabled={busy || !token} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6 disabled:opacity-50" data-testid="reset-submit">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Lock className="h-4 w-4 mr-2" /> Save new password</>}
            </Button>

            {!token && (
              <p className="text-xs text-red-400 mt-3 text-center" data-testid="reset-no-token">
                Missing reset token. <Link to="/forgot-password" className="underline">Request a new link.</Link>
              </p>
            )}

            <p className="text-sm text-zinc-500 text-center mt-5">
              <Link to="/login" className="text-zinc-500 hover:text-[#FF331F] uppercase tracking-widest font-bold text-xs inline-flex items-center gap-1" data-testid="reset-back-to-login">
                <ArrowLeft className="h-3 w-3" /> Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
